import { z } from 'zod'
import { BidParams } from './bid-params-mapping'
import { CatalogItemType } from './catalogItem'
import { DeviceType } from './device-type'
import { MediaType } from './media-types'
import { AdUnitEnv } from './pbs/adunit'
import { Site } from './site'

/**
 * `object` is not a value a publisher types. It declares a namespace: a param whose sub-keys the seat
 * defines instead of the catalog, for an adapter that takes free key-value pairs (OneTag's `ext`).
 *
 * It never becomes a mapping column itself. `expandObjectBidParams` replaces it with one column per
 * sub-key the seat declared, named `ext.customKey`. That is the ordinary dotted-param shape the rest of
 * the pipeline already stores flat and nests back when the stack is built.
 */
export const BID_PARAM_TYPES = ['number', 'string', 'boolean', 'array', 'object'] as const
export type BidParamType = (typeof BID_PARAM_TYPES)[number]

/** The types a publisher-defined sub-key can take. No `object`: a namespace cannot hold a namespace. */
export const OBJECT_SUB_PARAM_TYPES = ['number', 'string', 'boolean', 'array'] as const

/**
 * A custom key a seat declares under one of its catalog entry's `object` params.
 *
 * Not a `BidParamSpec` on purpose: a custom key is always optional. A required key would make every
 * mapping filled in before it was declared invalid, and the publisher could not save until they filled in
 * a key they never asked for. `expandObjectBidParams` gives each one `required: false`.
 */
export type ObjectSubParamSpec = {
  name: string
  type: (typeof OBJECT_SUB_PARAM_TYPES)[number]
}

export type BidderAlias = {
  bidderLabel: string
  bidderSource: string
  isServerSide?: boolean
}

export type Platform = 'client' | 'server'

export type BidParamSpec = {
  name: string
  required: boolean
  type: BidParamType
  platform?: Platform[]
}

export type BidParamValue = string | number | boolean | string[]

/**
 * A bid param whose value is decided once per seat rather than per mapping.
 *
 * Declared on the catalog entry as a template and copied onto the seat at creation (see
 * `resolveSeatBidParams`). `editable: false` makes the value a constant the publisher can neither
 * see nor change — the gingerad integration spec mandates `member: 12070` and `use_pmt_rule: true`
 * for every publisher, for instance.
 *
 * These never enter a mapping's `bidParams`, which is what keeps them out of the mapping
 * spreadsheet, both CSV exports, the CSV import and the mapping-export lambda: every one of those
 * derives its column list from the catalog's `bidParams`, not from this list.
 */
export type SeatBidParamSpec = {
  name: string
  type: BidParamType
  /** `false` => enforced: re-synced from the catalog on every write, never editable. */
  editable: boolean
  /** On a catalog entry: the template default. On a seat: the effective value, which staff may have changed when `editable`. */
  default?: BidParamValue
}

export type MarketPlaceProperties = {
  gdprQualification: string
  legalInformation: {
    processingDescription: string
    transferOutsideEU: string
    TCF: string
    restrictionDataReuse: string
    DpoContact: string
  }
  dsaCompatible: boolean
  privacyPolicies: string
  termsAndConditions: string
  dpa: string
}

export type BidderCatalog = {
  /**
   * The catalog entry's identity, and the only key anything joins on — `BidderSeat.bidderCode`
   * holds this, not `code`. Stable, unique (it is the mongo `_id`).
   */
  id: string
  displayName: string
  /**
   * The Prebid adapter this entry bids through. **Not unique**: a demand partner that rides another
   * adapter shares its code (`{id: 'gingerad', code: 'msft'}` alongside `{id: 'msft', code: 'msft'}`).
   * Use it to emit the adapter name — aliases, `pbjsModuleBidAdapters` — never to look an entry up
   * from a seat. Resolving a code back to an entry needs `getBidderCatalogByCode`'s tiebreak.
   */
  code: string
  bidParams: BidParamSpec[]
  isUsed: boolean
  type: CatalogItemType
  disableAliasing: boolean
  isServerSideCompatible?: boolean
  marketplace?: MarketPlaceProperties
  isPbstckBidderCompatible?: boolean
  isSparkForPubCompatible?: boolean
  tfshapingBidder?: string
  supportedMediaTypes?: MediaType[]
  disableSpecificMappingPerMediaType?: boolean
  disableBidAdjustment?: boolean
  mappingInfo?: string
  /** One line on what the company does, for the catalog card. Sourced from the company's own site. */
  tagline?: string
  /**
   * Where this bidder can run: web, s2s, inapp, amp. This is not `supportedMediaTypes`, which says
   * what the creative is: banner, video, native. A bidder declares both, and neither limits the other.
   *
   * Missing means the catalog has no data for this bidder. It never means the bidder runs nowhere.
   * Every reader shows more on a missing value, not less. Staff write it by hand in mongo.
   */
  supportedEnvs?: AdUnitEnv[]
  pbjsMinRequiredVersion?: string
  scopePbjsVersion?: string
  /** Template copied onto a seat at creation. See `SeatBidParamSpec`. */
  seatBidParams?: SeatBidParamSpec[]
  /** Seeded onto a seat at creation when the payload omits one — e.g. a partner bidding net-publisher. */
  defaultBidAdjustment?: {
    enabled: boolean
    value?: number
  }
}

/**
 * Whether the catalog says this bidder can run in `env`. A missing list is no data, never "nowhere" — see
 * `supportedEnvs` — so a bidder the catalog says nothing about runs everywhere. A list that leaves an env out
 * is the bidder saying it has no adapter there: Spark (`pbstck-bidder`) declares the server-side envs alone,
 * which is what keeps it off the client-side page without a reader that names it.
 */
export const catalogBidderRunsIn = (bidder: Pick<BidderCatalog, 'supportedEnvs'>, env: AdUnitEnv): boolean => bidder.supportedEnvs === undefined || bidder.supportedEnvs.includes(env)

/**
 * In-memory counterpart of `BidderCatalogStore.findOneByCode`: resolve a **Prebid adapter code** to
 * its catalog entry.
 *
 * `code` is not unique — a partner riding another adapter shares it — so prefer the adapter's own
 * entry (`id === code`) and fall back to the first match. Never use this with a seat's `bidderCode`,
 * which is an id.
 */
export const findCatalogByAdapterCode = (catalogBidders: BidderCatalog[], adapterCode: string | undefined): BidderCatalog | undefined =>
  adapterCode === undefined ? undefined : (catalogBidders.find((c) => c.code === adapterCode && c.id === adapterCode) ?? catalogBidders.find((c) => c.code === adapterCode))

/**
 * The seat params to merge into a mapping's bid params at stack generation.
 *
 * Enforced entries (`editable: false`) always win; editable ones fall back to the catalog default
 * when the seat carries no value. Entries are joined by `name`.
 */
export const resolveSeatBidParams = (seatBidParams: SeatBidParamSpec[] | undefined): Record<string, BidParamValue> =>
  Object.fromEntries((seatBidParams ?? []).filter((spec) => spec.default !== undefined).map((spec) => [spec.name, spec.default as BidParamValue]))

/**
 * The `seatBidParams` a seat should carry, given what the client submitted and what its catalog entry
 * declares. Used on both create and update, for both products.
 *
 * The catalog is authoritative for *which* params exist and for every enforced value: an entry the
 * catalog declares `editable: false` is always taken from the catalog, so a crafted payload cannot
 * change an enforced constant. Editable entries keep the seat's value when it has one, and fall back
 * to the catalog default otherwise. Entries the catalog no longer declares are dropped.
 */
export const reconcileSeatBidParams = (submitted: SeatBidParamSpec[] | undefined, catalogSeatBidParams: SeatBidParamSpec[] | undefined): SeatBidParamSpec[] | undefined => {
  if (!catalogSeatBidParams?.length) return undefined
  const submittedByName = new Map((submitted ?? []).map((spec) => [spec.name, spec]))
  return catalogSeatBidParams.map((catalogSpec) => {
    if (!catalogSpec.editable) return { ...catalogSpec }
    const submittedSpec = submittedByName.get(catalogSpec.name)
    return { ...catalogSpec, default: submittedSpec?.default ?? catalogSpec.default }
  })
}

/**
 * Apply a catalog entry's seat-level template (`seatBidParams`) to a seat about to be written.
 *
 * The catalog is authoritative: enforced entries are taken from it verbatim through
 * `reconcileSeatBidParams`, so a crafted payload cannot change one.
 *
 * An absent template is **deleted rather than set to `undefined`**: the PBS update path decides between
 * a cheap bid-adjustment patch and a full stack rebuild with `isEqual`, which treats `{}` and
 * `{ seatBidParams: undefined }` as different, and an undefined-valued key would also be persisted as a
 * pointless `null` on every seat that has no template.
 */
export const withSeatCatalogFields = <T extends { seatBidParams?: SeatBidParamSpec[]; extraObjectBidParams?: Record<string, ObjectSubParamSpec[]> }>(
  seat: T,
  catalogBidder: Pick<BidderCatalog, 'seatBidParams' | 'bidParams'> | null | undefined
): T => {
  const seatBidParams = reconcileSeatBidParams(seat.seatBidParams, catalogBidder?.seatBidParams)
  const extraObjectBidParams = reconcileExtraObjectBidParams(seat.extraObjectBidParams, catalogBidder?.bidParams)
  const result = { ...seat }
  if (seatBidParams === undefined) delete result.seatBidParams
  else result.seatBidParams = seatBidParams
  if (extraObjectBidParams === undefined) delete result.extraObjectBidParams
  else result.extraObjectBidParams = extraObjectBidParams
  return result
}

/**
 * Keep only the sub-keys that still belong: those under a namespace the catalog entry declares `object`.
 *
 * When the catalog drops a namespace, its keys go with it. Same rule `reconcileSeatBidParams` applies to
 * a retired seat param. This is a safety net, not the error path: the seat form runs
 * `validateExtraObjectBidParams` and warns before anyone submits.
 *
 * An empty result is deleted rather than set to `undefined`, for the reason `withSeatCatalogFields`
 * gives: the PBS update path compares seats with `isEqual`, which reads an undefined-valued key as a
 * difference, and it would be persisted as a useless `null`.
 */
export const reconcileExtraObjectBidParams = (
  submitted: Record<string, ObjectSubParamSpec[]> | undefined,
  catalogBidParams: BidParamSpec[] | undefined
): Record<string, ObjectSubParamSpec[]> | undefined => {
  if (!submitted) return undefined
  const namespaces = new Set((catalogBidParams ?? []).filter((spec) => spec.type === 'object').map((spec) => spec.name))
  const kept = Object.entries(submitted).filter(([namespace, subParams]) => namespaces.has(namespace) && subParams.length > 0)
  return kept.length ? Object.fromEntries(kept) : undefined
}

/** Marks a client-side seat that Pubstack sells through the marketplace. Only Pubstack users create one. */
export const marketplaceAliasSuffix = '-pbstck'

export const isMarketplaceAlias = (aliasLabel: string) => aliasLabel.endsWith(marketplaceAliasSuffix)

export type BidderCatalogAlias = BidderAlias & {
  isUsed: boolean
  bidderParams: BidderParam[]
  bidParams: BidParamSpec[]
  bidAdjustment?: number
  bidderSeatId: string
}

export type BidderParam = {
  adUnitName: string
  bidderName: string
  device: DeviceType
  params: BidParams
  siteName: Site['name']
}

export const EMPTY_MARKETPLACE_PROPERTIES: MarketPlaceProperties = {
  gdprQualification: '',
  legalInformation: {
    processingDescription: '',
    transferOutsideEU: '',
    TCF: '',
    restrictionDataReuse: '',
    DpoContact: '',
  },
  dsaCompatible: false,
  privacyPolicies: '',
  termsAndConditions: '',
  dpa: '',
}

export const SparkForPubAvailabilityUpdateSchema = z.object({
  id: z.string().min(1),
  enabled: z.boolean(),
})
export type SparkForPubAvailabilityUpdate = z.infer<typeof SparkForPubAvailabilityUpdateSchema>

export const TfshapingBidderUpdateSchema = z.object({
  id: z.string().min(1),
  tfshapingBidder: z.string().trim().min(1),
})
export type TfshapingBidderUpdate = z.infer<typeof TfshapingBidderUpdateSchema>
